<template>
  <div
    class="relative min-h-screen w-full py-3 lg:py-6 lg:px-3"
    :class="$slots.cta ? '!pb-28 lg:!pb-6' : ''"
  >
    <!-- Darkmode Toggle + PHX Logo on Mobile -->
    <div class="lg:absolute top-6 right-6 z-10 flex justify-between w-full lg:w-auto">
      <div class="flex items-center gap-4">
        <Button
          v-if="isShowBackButton"
          @click="handleBack"
          variant="paper-primary"
          class="lg:hidden"
        >
          <template #icon>
            <Icon name="ChevronLeft" class="h-[22px] w-[22px]" />
          </template>
        </Button>
        <IconPHXLogo
          class="h-10 lg:hidden"
          :class="UIStore.is_dark_mode ? 'text-white' : 'text-black'"
        />
      </div>

      <div class="flex items-center gap-2">
        <Button @click="handleCallClick" size="md-sm" variant="paper-primary">
          <template #label>
            <span>
              <span class="hidden sm:inline font-inter">Call 855-732-6395</span>
              <span class="sm:hidden font-inter">Call Us</span>
            </span>
          </template>
          <template #icon>
            <Icon name="Phone" class="h-5 w-5" :stroke-width="1.5" />
          </template>
        </Button>
        <Button variant="paper-primary" size="md-sm" @click="UIStore.toggleDarkMode">
          <template #icon>
            <Icon :name="UIStore.is_dark_mode ? 'Sun' : 'Moon'" class="w-5 h-5 mb-0.5" />
          </template>
        </Button>
      </div>
    </div>

    <div
      class="flex min-h-[calc(100vh-24px)] lg:min-h-[calc(100vh-48px)] lg:max-h-[calc(100vh-48px)] flex-col lg:flex-row-reverse"
    >
      <!-- FORM COLUMN -->
      <div class="flex w-full lg:w-[55%] xl:w-[65%] justify-center items-center">
        <div
          class="w-full lg:max-w-[450px] lg:min-h-[500px] flex flex-col items-start text-strongest"
        >
          <AuthStepper v-if="isSignupPage" :numSteps="5" class="mt-[1.625rem] lg:mt-0" />
          <div class="mb-8" :class="isSignupPage ? 'mt-8' : 'mt-[1.625rem]'">
            <slot name="header" />
          </div>
          <slot />
        </div>
      </div>

      <!-- TESTIMONIAL COLUMN -->
      <div
        class="w-full mt-[3.75rem] lg:mt-0 lg:w-[45%] xl:w-[35%] min-h-0 overflow-hidden"
        :class="!isShowTestimonials ? 'hidden lg:flex' : ''"
      >
        <InvestorTestimonials />
      </div>
    </div>

    <!-- Mobile Fixed Bottom CTA -->
    <div
      v-if="$slots.cta"
      class="fixed inset-x-0 z-30 lg:hidden btn-bg-swap bg-purchase-flow-bottom-nav-gradient rounded-t-3xl p-3"
      :style="{
        bottom: `${keyboardInset}px`,
        paddingBottom: `calc(0.75rem + env(safe-area-inset-bottom))`,
      }"
    >
      <slot name="cta" />
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { useRoute, useRouter } from "vue-router";

import { useUIStore, useAuthStore } from "@/stores";
import { Button, Icon } from "@/components";
import { IconPHXLogo } from "@/components/icons";
import { useKeyboardInset } from "@/composables/useKeyboardInset";

import InvestorTestimonials from "./InvestorTestimonials.vue";
import AuthStepper from "./AuthStepper.vue";

const route = useRoute();
const router = useRouter();

const authStore = useAuthStore();
const UIStore = useUIStore();
const keyboardInset = useKeyboardInset();

const routeMap = {
  "/sign-up": 1,
  "/sign-up/personal-details": 2,
  "/sign-up/set-password": 3,
  "/sign-up/acknowledgements": 4,
  "/sign-up/auth-verification": 5,
};

const isSignupPage = computed(() => {
  return route.path.includes("sign-up");
});

const isShowTestimonials = computed(() => {
  if (route.path.includes("sign-up")) {
    return authStore.active_signup_step === 1;
  } else if (route.path.includes("sign-in")) {
    return authStore.active_signin_step === 1;
  } else if (route.path.includes("forgot-password")) {
    return true;
  } else if (route.path.includes("change-password")) {
    return true;
  }
  return false;
});

const isShowBackButton = computed(() => {
  if (route.path.includes("sign-up")) {
    return authStore.active_signup_step > 1;
  } else if (route.path.includes("sign-in")) {
    return authStore.active_signin_step > 1;
  } else if (route.path.includes("forgot-password")) {
    return true;
  } else if (route.path.includes("change-password")) {
    return true;
  }
  return false;
});

function handleBack() {
  if (route.path.includes("auth-verification") && authStore.mfa_channel && !authStore.is_signup) {
    // Special case for navigating back from MFA step - need to reset mfa_channel to trigger correct flow in SetPassword step
    authStore.mfa_channel = null;
    return;
  }
  authStore.active_signup_step = routeMap[route.path] - 1 || 1; // Default to step 1 if path not found
  router.back();
}

const handleCallClick = () => {
  window.location.href = "tel:855-732-6395";
};
</script>
